import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../components/auth/AuthContext";
import { HeaderBar } from "../components/layout/TopBar";
import NavBar from "../components/layout/NavBar";
import { PulseArt } from "../components/adminSurface/heroArt";
import { Hero, SegControl, EmptyState } from "../components/adminSurface/AdminSurface";
import { useIsAdmin } from "../hooks/useFeatureFlags";
import WorkflowDesigner from "../components/workflow/WorkflowDesigner";
import WorkflowRules from "../components/workflow/WorkflowRules";
import ConfirmModal from "../components/common/ConfirmModal";
import PromptModal from "../components/common/PromptModal";
import "../styles/workflow.css";
import {
  getWorkflowDefinitions,
  getWorkflowDefinition,
  createWorkflowDefinition,
  saveWorkflowGraph,
  validateWorkflowDefinition,
  activateWorkflowDefinition,
  archiveWorkflowDefinition,
  getWorkflowInstances,
  getWorkflowInstance,
  cancelWorkflowInstance,
  getWorkflowAssigneeSuggestions,
} from "../api/adminApi";
import { getGroups } from "../api/groupRightsApi";

/**
 * Workflow / BPM admin page: design a workflow, set the rules that start it, then watch its cases run.
 *
 * Design and Cases are deliberately the same picture. A definition and a running instance are drawn by the
 * SAME layout code, so "what will happen" and "what did happen" are visually comparable rather than two
 * different diagrams the reader has to reconcile. Rules is the third side of that: what makes a case appear
 * without anyone pressing anything.
 */
export default function WorkflowPage() {
  const { t } = useTranslation();
  const { token } = useAuth();
  const isAdmin = useIsAdmin();

  const [tab, setTab] = useState("design");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const [definitions, setDefinitions] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [graph, setGraph] = useState({ nodes: [], edges: [] });
  const [definition, setDefinition] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [validation, setValidation] = useState(null);
  const [roles, setRoles] = useState([]);
  // Who this org already routes work to, for the assignee picker's recents/frequents. Loaded once with the
  // page rather than when a picker opens: it is one small request, and a dialog that has to wait on a network
  // round trip before showing anything is a dialog people stop opening.
  const [assigneeSuggestions, setAssigneeSuggestions] = useState(null);

  // Bumped by the hero refresh so the rules tab can reload without lifting its state into this page.
  const [rulesReload, setRulesReload] = useState(0);

  // In-app dialogs. window.prompt/confirm draw the browser's own chrome, which ignores the design system.
  const [namePrompt, setNamePrompt] = useState(false);
  const [archiveConfirm, setArchiveConfirm] = useState(false);
  const [cancelPrompt, setCancelPrompt] = useState(null); // instance id awaiting a cancellation reason

  const [instances, setInstances] = useState([]);
  const [instanceId, setInstanceId] = useState(null);
  const [instanceDetail, setInstanceDetail] = useState(null);

  const say = useCallback((msg) => {
    setNotice(msg);
    setError(null);
  }, []);
  const fail = useCallback((e) => {
    setError(e?.message || String(e));
    setNotice(null);
  }, []);

  // ── Loading ─────────────────────────────────────────────────────────────────────────────

  const loadDefinitions = useCallback(async () => {
    try {
      const rows = await getWorkflowDefinitions(token);
      setDefinitions(Array.isArray(rows) ? rows : []);
      return rows;
    } catch (e) {
      fail(e);
      return [];
    }
  }, [token, fail]);

  const loadDefinition = useCallback(
    async (id) => {
      if (!id) return;
      setBusy(true);
      try {
        const d = await getWorkflowDefinition(token, id);
        setDefinition(d.definition ?? d.Definition ?? null);
        setGraph({ nodes: d.nodes ?? d.Nodes ?? [], edges: d.edges ?? d.Edges ?? [] });
        setDirty(false);
        setValidation(null);
      } catch (e) {
        fail(e);
      } finally {
        setBusy(false);
      }
    },
    [token, fail]
  );

  useEffect(() => {
    if (!token) return;
    loadDefinitions();
    // Role pools come from the same SearchRole groups the access-control matrix uses.
    getGroups(token)
      .then((r) => setRoles(Array.isArray(r) ? r : r?.groups ?? []))
      .catch(() => setRoles([]));
    // Advisory only — a failure here costs the picker its recents, not its ability to name somebody.
    getWorkflowAssigneeSuggestions(token)
      .then((r) => setAssigneeSuggestions(r ?? null))
      .catch(() => setAssigneeSuggestions(null));
  }, [token, loadDefinitions]);

  useEffect(() => {
    if (selectedId) loadDefinition(selectedId);
  }, [selectedId, loadDefinition]);

  const loadInstances = useCallback(async () => {
    try {
      const res = await getWorkflowInstances(token, { take: 100 });
      setInstances(res?.items ?? res?.Items ?? []);
    } catch (e) {
      fail(e);
    }
  }, [token, fail]);

  useEffect(() => {
    if (tab === "runs" && token) loadInstances();
  }, [tab, token, loadInstances]);

  useEffect(() => {
    if (!instanceId) {
      setInstanceDetail(null);
      return;
    }
    getWorkflowInstance(token, instanceId).then(setInstanceDetail).catch(fail);
  }, [instanceId, token, fail]);

  // ── Actions ─────────────────────────────────────────────────────────────────────────────

  const onCreate = useCallback(async (name) => {
    setNamePrompt(false);
    if (!name) return;
    setBusy(true);
    try {
      const created = await createWorkflowDefinition(token, { name, subjectKind: "Document" });
      // A workflow is born with a Start and an End so the canvas is never a blank rectangle nobody can act on.
      await saveWorkflowGraph(token, created.id, {
        nodes: [
          { nodeKey: "start", nodeType: "Start", name: t("workflow.node.start", { defaultValue: "Start" }) },
          { nodeKey: "end", nodeType: "End", name: t("workflow.node.approved", { defaultValue: "Approved" }), outcomeLabel: "Approved" },
        ],
        edges: [{ fromNodeKey: "start", toNodeKey: "end" }],
      });
      await loadDefinitions();
      setSelectedId(created.id);
      setTab("design");
      say(t("workflow.msg.created", { defaultValue: "Workflow created as a draft." }));
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  }, [token, t, loadDefinitions, say, fail]);

  const onSave = useCallback(async () => {
    if (!selectedId) return;
    setBusy(true);
    try {
      const res = await saveWorkflowGraph(token, selectedId, graph);
      const newId = res?.definition?.id;
      await loadDefinitions();
      setDirty(false);
      if (res?.newVersion && newId && newId !== selectedId) {
        // Editing an Active workflow does not change it — it creates the next version, and the person needs
        // to be told plainly rather than wondering why the old one still shows as Active.
        setSelectedId(newId);
        say(
          t("workflow.msg.newVersion", {
            defaultValue:
              "That workflow is live, so your changes were saved as version {{v}} (a new draft). Activate it when you are ready — running cases keep using the version they started on.",
            v: res.definition.version,
          })
        );
      } else {
        await loadDefinition(selectedId);
        say(t("workflow.msg.saved", { defaultValue: "Saved." }));
      }
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  }, [token, selectedId, graph, loadDefinitions, loadDefinition, say, fail, t]);

  const onValidate = useCallback(async () => {
    if (!selectedId) return;
    try {
      const v = await validateWorkflowDefinition(token, selectedId);
      setValidation(v);
      if (v?.isValid) say(t("workflow.msg.valid", { defaultValue: "This workflow is ready to activate." }));
      else setNotice(null);
    } catch (e) {
      fail(e);
    }
  }, [token, selectedId, say, fail, t]);

  const onActivate = useCallback(async () => {
    if (!selectedId) return;
    setBusy(true);
    try {
      await activateWorkflowDefinition(token, selectedId);
      await loadDefinitions();
      await loadDefinition(selectedId);
      setValidation(null);
      say(t("workflow.msg.activated", { defaultValue: "Activated. New cases will use this version." }));
    } catch (e) {
      // The server refuses with the exact reasons; show them on the canvas rather than a bare failure.
      if (e?.details?.errors) setValidation({ isValid: false, errors: e.details.errors });
      fail(e);
    } finally {
      setBusy(false);
    }
  }, [token, selectedId, loadDefinitions, loadDefinition, say, fail, t]);

  const onArchive = useCallback(async () => {
    setArchiveConfirm(false);
    if (!selectedId) return;
    setBusy(true);
    try {
      const res = await archiveWorkflowDefinition(token, selectedId);
      await loadDefinitions();
      await loadDefinition(selectedId);
      say(
        t("workflow.msg.archived", {
          defaultValue: "Archived. {{n}} running case(s) are unaffected.",
          n: res?.runningInstancesUnaffected ?? 0,
        })
      );
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  }, [token, selectedId, loadDefinitions, loadDefinition, say, fail, t]);

  const onCancelInstance = useCallback(
    async (id, reason) => {
      setCancelPrompt(null);
      if (!id) return;
      try {
        await cancelWorkflowInstance(token, id, { reason });
        await loadInstances();
        if (instanceId === id) setInstanceId(id);
        say(t("workflow.msg.cancelled", { defaultValue: "Case cancelled." }));
      } catch (e) {
        fail(e);
      }
    },
    [token, loadInstances, instanceId, say, fail, t]
  );

  const status = definition?.status ?? definition?.Status;
  const isActive = status === "Active";
  const isArchived = status === "Archived";

  const activeCount = useMemo(
    () => definitions.filter((d) => (d.status ?? d.Status) === "Active").length,
    [definitions]
  );

  if (!isAdmin) {
    return (
      <>
        <HeaderBar />
        <NavBar />
        <div className="page-container">
          <EmptyState
            icon="🔒"
            title={t("workflow.noAccess", { defaultValue: "Administrator access required" })}
            hint={t("workflow.noAccessHint", { defaultValue: "Designing workflows is an administrator function. Your own tasks are on the Notifications page." })}
          />
        </div>
      </>
    );
  }

  return (
    <>
      <HeaderBar />
      <NavBar />
      <div className="page-container">
        <Hero
          icon="🔀"
          title={t("workflow.title", { defaultValue: "Workflow" })}
          subtitle={t("workflow.subtitle", { defaultValue: "Design review and approval routing, then watch cases run." })}
          statValue={String(activeCount)}
          statLabel={t("workflow.statActive", { defaultValue: "live workflows" })}
          art={<PulseArt />}
          onRefresh={() => {
            if (tab === "runs") loadInstances();
            else if (tab === "rules") { loadDefinitions(); setRulesReload((n) => n + 1); }
            else loadDefinitions();
          }}
          refreshing={busy}
        />

        <div className="asf-card">
          <div className="wf-bar">
            <SegControl
              value={tab}
              onChange={setTab}
              options={[
                { value: "design", label: t("workflow.tab.design", { defaultValue: "Design" }) },
                { value: "rules", label: t("workflow.tab.rules", { defaultValue: "Rules" }) },
                { value: "runs", label: t("workflow.tab.runs", { defaultValue: "Cases" }) },
              ]}
            />
            <div className="wf-bar-spacer" />
            {tab === "design" && (
              <>
                <button className="asf-btn" onClick={() => setNamePrompt(true)} disabled={busy}>
                  {t("workflow.action.new", { defaultValue: "New workflow" })}
                </button>
                {selectedId && !isArchived && (
                  <>
                    <button className="asf-btn" onClick={onSave} disabled={busy || !dirty}>
                      {t("workflow.action.save", { defaultValue: "Save" })}
                    </button>
                    <button className="asf-btn" onClick={onValidate} disabled={busy}>
                      {t("workflow.action.check", { defaultValue: "Check" })}
                    </button>
                    {!isActive && (
                      <button className="asf-cta" onClick={onActivate} disabled={busy || dirty}>
                        {t("workflow.action.activate", { defaultValue: "Activate" })}
                      </button>
                    )}
                    <button className="asf-btn" onClick={() => setArchiveConfirm(true)} disabled={busy}>
                      {t("workflow.action.archive", { defaultValue: "Archive" })}
                    </button>
                  </>
                )}
              </>
            )}
          </div>

          {error && <div className="wf-warn">{error}</div>}
          {notice && <div className="wf-ok">{notice}</div>}

          {tab === "rules" ? (
            <WorkflowRules
              token={token}
              definitions={definitions}
              reloadKey={rulesReload}
              say={say}
              fail={fail}
            />
          ) : tab === "design" ? (
            <div className="wf-grid">
              <div>
                <p className="wf-note">
                  {t("workflow.designHint", {
                    defaultValue:
                      "Steps are laid out automatically — use + between two steps to add one. A live workflow cannot be edited in place: saving creates the next version.",
                  })}
                </p>
                <div className="wf-list">
                  {definitions.length === 0 && (
                    <EmptyState
                      icon="🔀"
                      title={t("workflow.empty.title", { defaultValue: "No workflows yet" })}
                      hint={t("workflow.empty.hint", { defaultValue: "Create one to start routing documents for approval." })}
                    />
                  )}
                  {definitions.map((d) => {
                    const s = d.status ?? d.Status;
                    return (
                      <button
                        key={d.id}
                        className={`wf-list-item${selectedId === d.id ? " is-on" : ""}`}
                        onClick={() => setSelectedId(d.id)}
                      >
                        <span>
                          <span className="wf-list-name">{d.name ?? d.Name}</span>
                          <br />
                          <span className="wf-list-sub">
                            v{d.version ?? d.Version} · {d.instanceCount ?? d.InstanceCount ?? 0}{" "}
                            {t("workflow.cases", { defaultValue: "cases" })}
                          </span>
                        </span>
                        <span className={`wf-pill is-${String(s).toLowerCase()}`}>{s}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                {!selectedId ? (
                  <EmptyState
                    icon="👈"
                    title={t("workflow.pick.title", { defaultValue: "Pick a workflow" })}
                    hint={t("workflow.pick.hint", { defaultValue: "Choose one on the left, or create a new one." })}
                  />
                ) : (
                  <>
                    {isActive && (
                      <div className="wf-note">
                        {t("workflow.liveHint", {
                          defaultValue:
                            "This version is live. Any change you save becomes a new draft version — cases already running keep the version they started on.",
                        })}
                      </div>
                    )}
                    {isArchived && (
                      <div className="wf-warn">
                        {t("workflow.archivedHint", { defaultValue: "This workflow is archived and cannot be edited or started." })}
                      </div>
                    )}
                    {definition?.validationError && !validation && (
                      <div className="wf-warn">{definition.validationError}</div>
                    )}
                    <WorkflowDesigner
                      nodes={graph.nodes}
                      edges={graph.edges}
                      roles={roles}
                      directoryToken={token}
                      assigneeSuggestions={assigneeSuggestions}
                      readOnly={isArchived}
                      validation={validation}
                      onChange={(nodes, edges) => {
                        setGraph({ nodes, edges });
                        setDirty(true);
                        setValidation(null); // stale the moment the graph changes
                      }}
                    />
                  </>
                )}
              </div>
            </div>
          ) : (
            <div className="wf-grid">
              <div className="wf-list">
                {instances.length === 0 && (
                  <EmptyState
                    icon="🗂"
                    title={t("workflow.noRuns.title", { defaultValue: "No cases yet" })}
                    hint={t("workflow.noRuns.hint", { defaultValue: "Cases appear here once a workflow starts on a document." })}
                  />
                )}
                {instances.map((i) => {
                  const s = i.status ?? i.Status;
                  return (
                    <button
                      key={i.id}
                      className={`wf-list-item${instanceId === i.id ? " is-on" : ""}`}
                      onClick={() => setInstanceId(i.id)}
                    >
                      <span>
                        <span className="wf-list-name">{i.subjectTitle || i.archiveNo || `#${i.id}`}</span>
                        <br />
                        <span className="wf-list-sub">
                          {i.definitionName} {i.startedByRule ? `· ${t("workflow.byRule", { defaultValue: "auto-started" })}` : ""}
                        </span>
                      </span>
                      <span className={`wf-pill is-${String(s).toLowerCase()}`}>{s}</span>
                    </button>
                  );
                })}
              </div>

              <div>
                {!instanceDetail ? (
                  <EmptyState
                    icon="👈"
                    title={t("workflow.pickRun.title", { defaultValue: "Pick a case" })}
                    hint={t("workflow.pickRun.hint", { defaultValue: "Its progress is drawn with the same picture as the design." })}
                  />
                ) : (
                  <>
                    <div className="wf-bar">
                      <strong>{instanceDetail.instance?.subjectTitle || instanceDetail.instance?.archiveNo || `#${instanceDetail.instance?.id}`}</strong>
                      <span className={`wf-pill is-${String(instanceDetail.instance?.status).toLowerCase()}`}>
                        {instanceDetail.instance?.status}
                      </span>
                      <div className="wf-bar-spacer" />
                      {instanceDetail.instance?.status === "Running" && (
                        <button className="asf-btn" onClick={() => setCancelPrompt(instanceDetail.instance.id)}>
                          {t("workflow.action.cancel", { defaultValue: "Cancel case" })}
                        </button>
                      )}
                    </div>
                    <WorkflowDesigner
                      nodes={instanceDetail.nodes ?? []}
                      edges={instanceDetail.edges ?? []}
                      tasks={instanceDetail.tasks ?? []}
                      instance={instanceDetail.instance}
                      roles={roles}
                      directoryToken={token}
                      assigneeSuggestions={assigneeSuggestions}
                      readOnly
                    />
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <PromptModal
        open={namePrompt}
        icon="🔀"
        title={t("workflow.prompt.nameTitle", { defaultValue: "New workflow" })}
        label={t("workflow.prompt.name", { defaultValue: "Name this workflow" })}
        placeholder={t("workflow.prompt.namePlaceholder", { defaultValue: "e.g. Invoice approval" })}
        confirmLabel={t("workflow.prompt.nameConfirm", { defaultValue: "Create" })}
        maxLength={200}
        busy={busy}
        onSubmit={onCreate}
        onCancel={() => setNamePrompt(false)}
      />

      <ConfirmModal
        open={archiveConfirm}
        variant="warn"
        icon="📦"
        title={t("workflow.confirm.archiveTitle", { defaultValue: "Archive this workflow?" })}
        name={definition?.name ?? definition?.Name}
        body={t("workflow.confirm.archive", { defaultValue: "Archive this workflow? Cases already running are unaffected and will finish normally." })}
        confirmLabel={t("workflow.action.archive", { defaultValue: "Archive" })}
        onConfirm={onArchive}
        onCancel={() => setArchiveConfirm(false)}
      />

      <PromptModal
        open={cancelPrompt != null}
        variant="warn"
        icon="🛑"
        title={t("workflow.prompt.cancelTitle", { defaultValue: "Cancel this case?" })}
        description={t("workflow.prompt.cancelDesc", { defaultValue: "Open tasks are closed and the case stops where it is. This cannot be undone." })}
        label={t("workflow.prompt.cancel", { defaultValue: "Why is this case being cancelled?" })}
        placeholder={t("workflow.prompt.cancelPlaceholder", { defaultValue: "e.g. Raised in error" })}
        confirmLabel={t("workflow.action.cancel", { defaultValue: "Cancel case" })}
        multiline
        required={false}
        maxLength={500}
        onSubmit={(reason) => onCancelInstance(cancelPrompt, reason)}
        onCancel={() => setCancelPrompt(null)}
      />
    </>
  );
}
